import React, { useEffect, useState } from 'react';
import {
  Input, Select, Row, Col, DatePicker,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AlumlyButton from '../../../components/AlumlyButton';
import { CustomerEnum } from '../../../enums/customer';
import { InputFormatEnum } from '../../../enums/inputFormat';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';

type AccessMethodT = {
  id: number;
  name: string;
  description: string;
  isDeleted: boolean;
};

type DropdownDetailT = {
  label: string;
  value: string | number;
};

const memberList: DropdownDetailT[] = [
  {
    label: 'Club card',
    value: CustomerEnum.member.clubCardMember,
  },
  {
    label: 'Non-club card',
    value: CustomerEnum.member.nonClubCard,
  },
];
const statusList: DropdownDetailT[] = [
  {
    label: 'Active',
    value: 'active',
  },
  {
    label: 'Inactive',
    value: 'inactive',
  },
];
const alumniStatusList: DropdownDetailT[] = [
  {
    label: 'Pending approval',
    value: 'pending',
  },
  {
    label: 'Approved',
    value: 'approved',
  },
  {
    label: 'Rejected',
    value: 'rejected',
  },
];

export type FilterCustomerListT = {
  searchQuery: string,
  registeredType: string,
  gender: string,
  createdAtFromDate: Date,
  createdAtToDate: Date,
  status: string,
  memberStatus: number,
  alumniStatus: string,
};

interface FormFilterCustomerListProps {
  resetPage: Function;
  selectedFilter: FilterCustomerListT | undefined;
  setSelectedFilter: React.Dispatch<React.SetStateAction<any>>;
}

const FormFilterCustomerList: React.FC<FormFilterCustomerListProps> = (props: FormFilterCustomerListProps) => {
  const { selectedFilter, setSelectedFilter, resetPage } = props;

  const [accessMethodList, setAccessMethodList] = useState<DropdownDetailT[]>();

  const { service: getAccessMethodListService } = useServices(ServiceNameEnum.accessMethod.index);

  const { control, reset } = useForm({
    defaultValues: {
      searchQuery: '',
      registeredType: '',
      memberStatus: null,
      createdAtFromDate: null as Dayjs | null,
      createdAtToDate: null as Dayjs | null,
      status: '',
      alumniStatus: '',
    },
  });

  const loadAccessMethodList = async (): Promise<void> => {
    const { data, error } = await getAccessMethodListService.get();
    if (data) {
      const formattedData: DropdownDetailT[] = data.map((datum: AccessMethodT) => ({
        label: datum.description,
        value: datum.id,
      }));

      setAccessMethodList(formattedData);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  useEffect(() => {
    loadAccessMethodList();
  }, []);

  const handleSearch = (searchQuery: string): void => {
    if (searchQuery.length % 3 === 0) {
      setSelectedFilter({ ...selectedFilter, searchQuery });
      resetPage();
    }
  };

  const resetSearchAndFilter = (): void => {
    reset();
    setSelectedFilter({
      searchQuery: '',
      registeredType: '',
      memberStatus: null,
      createdAtFromDate: null,
      createdAtToDate: null,
      status: '',
      alumniStatus: '',
    });
  };

  return (
    <div style={{
      padding: '16px 24px',
      background: 'white',
      border: '1px solid #E4E4E7',
      borderRadius: 12,
      paddingTop: 32,
      paddingBottom: 32,
    }}
    >
      <Row gutter={16} align="middle" style={{ rowGap: 16 }}>
        <Col xs={24}>
          <Controller
            name="searchQuery"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined style={{ color: '#A1A1AA' }} />}
                placeholder="Search by First name, Last name or Email"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  handleSearch(e.target.value);
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6}>
          <Controller
            name="createdAtFromDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                style={{ width: '100%' }}
                size="large"
                format={InputFormatEnum.date.format}
                placeholder="Created date from"
                value={value}
                onChange={(date) => {
                  onChange(date);
                  setSelectedFilter({
                    ...selectedFilter,
                    createdAtFromDate: DateTimeUtils.dateFormat(date?.toDate() || undefined),
                  });
                  resetPage();
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6}>
          <Controller
            name="createdAtToDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                style={{ width: '100%' }}
                size="large"
                format={InputFormatEnum.date.format}
                placeholder="Created date to"
                value={value}
                onChange={(date) => {
                  onChange(date);
                  setSelectedFilter({
                    ...selectedFilter,
                    createdAtToDate: DateTimeUtils.dateFormat(date?.toDate() || undefined),
                  });
                  resetPage();
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6}>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder="Status"
                allowClear
                value={value || undefined}
                onChange={(val) => {
                  onChange(val ?? '');
                  setSelectedFilter({ ...selectedFilter, status: val });
                  resetPage();
                }}
                options={statusList.map((status) => ({ value: status.value, label: status.label }))}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6}>
          <Controller
            name="alumniStatus"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder="Alumni status"
                allowClear
                value={value || undefined}
                onChange={(val) => {
                  onChange(val ?? '');
                  setSelectedFilter({ ...selectedFilter, alumniStatus: val });
                  resetPage();
                }}
                options={alumniStatusList.map((s) => ({ value: s.value, label: s.label }))}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AlumlyButton label="rxvWhite" onClick={resetSearchAndFilter}>
            Reset filters
          </AlumlyButton>
        </Col>
      </Row>
    </div>
  );
};

export default FormFilterCustomerList;
